import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import type { SuggestionItem } from '@/entities/feedback';
import { formatDateTime } from '@/shared/lib/datetime';

import { categoryLabel } from './labels';
import { StatusBadge } from './StatusBadge';
import styles from './SuggestionCard.module.css';

interface Props {
  item: SuggestionItem;
  /** The moderation list also shows who wrote it, who owns it and whether it needs a look. */
  staff: boolean;
  timeZone?: string | undefined;
  /** Where the detail page should send "back" (the list with its filters). */
  from: string;
}

/** One suggestion in a list. The status icon is still here: motion is for the detail page. */
export function SuggestionCard({ item, staff, timeZone, from }: Props) {
  const { t, i18n } = useTranslation();
  return (
    <li className={styles.item}>
      <Link
        to={`/app/feedback/${item.id}`}
        state={{ from }}
        className={`${styles.card} ${item.unread_count > 0 ? styles.unread : ''}`}
        data-testid="feedback-card"
      >
        <span className={styles.top}>
          <span className={styles.title}>{item.title}</span>
          <span className={styles.number}>#{item.number}</span>
        </span>
        <span className={styles.meta}>
          <span className={styles.category}>{categoryLabel(t, item.category)}</span>
          {staff && item.author && (
            <span className={styles.person}>
              {t('feedback.card.by', { name: item.author.display_name })}
            </span>
          )}
        </span>
        <span className={styles.preview}>{item.preview}</span>
        <span className={styles.bottom}>
          <StatusBadge status={item.status} />
          <span className={styles.flags}>
            {item.withdrawn && <span className={styles.flag}>{t('feedback.card.withdrawn')}</span>}
            {staff && item.needs_attention && (
              <span className={`${styles.flag} ${styles.attention}`}>
                {t('feedback.card.attention')}
              </span>
            )}
            {staff && item.assignee && (
              <span className={styles.flag}>
                {t('feedback.card.assignedTo', { name: item.assignee.display_name })}
              </span>
            )}
            {item.unread_count > 0 && (
              <span className={`${styles.flag} ${styles.newReply}`}>
                {t('feedback.card.unread', { count: item.unread_count })}
              </span>
            )}
            {item.public_message_count > 0 && (
              <span className={styles.count}>
                {t('feedback.card.messages', { count: item.public_message_count })}
              </span>
            )}
          </span>
          <time className={styles.when} dateTime={item.last_activity_at}>
            {formatDateTime(item.last_activity_at, i18n.language, timeZone)}
          </time>
        </span>
      </Link>
    </li>
  );
}
